"use client";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/components/ui/collapsible";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
} from "@shared/components/ui/sidebar";
import { ChevronRightIcon } from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { Badge } from "@shared/components/ui/badge";
import { cn } from "@/lib/utils";

export function NavMain({ items }) {
  const { t } = useTranslation();

  return (
    <SidebarMenu className="space-y-1">
      {items.map((item) =>
        item.items ? (
          <Collapsible
            key={item.title}
            defaultOpen={item.isActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip={t(item.title)}
                  className={cn(
                    "h-10 px-3 py-2 rounded-lg transition-all duration-200",
                    "hover:bg-sidebar-accent/50 group-data-[state=open]/collapsible:bg-sidebar-accent/30",
                    "focus:outline-none focus:ring-2 focus:ring-sidebar-primary/20",
                    "sidebar-menu-item"
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      {item.icon && (
                        <item.icon className="h-4 w-4 text-sidebar-foreground/70" />
                      )}
                      <span className="font-medium text-sm">
                        {t(item.title)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.badge && (
                        <Badge
                          variant="default"
                          className="h-5 px-2 text-xs font-semibold sidebar-badge"
                        >
                          {item.badge}
                        </Badge>
                      )}
                      <ChevronRightIcon className="h-4 w-4 text-sidebar-foreground/50 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </div>
                  </div>
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent className="transition-all duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 collapsible-content">
                <SidebarMenuSub className="ml-6 mt-2 space-y-1">
                  {item.items?.map((subItem) => (
                    <NavLink to={subItem.url} key={subItem.title} end>
                      {({ isActive }) => (
                        <SidebarMenuSubButton
                          isActive={isActive}
                          asChild
                          className={cn(
                            "h-8 px-3 py-1 rounded-md transition-all duration-200",
                            "hover:bg-sidebar-accent/30",
                            isActive &&
                              "bg-sidebar-primary/10 text-sidebar-primary font-medium border-l-2 border-sidebar-primary"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            {subItem.icon && (
                              <subItem.icon className="h-3.5 w-3.5" />
                            )}
                            <span className="text-sm">{t(subItem.title)}</span>
                          </div>
                        </SidebarMenuSubButton>
                      )}
                    </NavLink>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ) : (
          <SidebarMenuItem key={item.title}>
            <NavLink to={item.url} end>
              {({ isActive }) => (
                <SidebarMenuButton
                  isActive={isActive}
                  className={cn(
                    "h-10 px-3 py-2 rounded-lg transition-all duration-200",
                    "hover:bg-sidebar-accent/50",
                    "focus:outline-none focus:ring-2 focus:ring-sidebar-primary/20",
                    "sidebar-menu-item",
                    isActive &&
                      "bg-sidebar-primary/10 text-sidebar-primary font-medium shadow-sm border-l-2 border-sidebar-primary sidebar-active"
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <item.icon className="h-4 w-4" />
                      <span className="font-medium text-sm">
                        {t(item.title)}
                      </span>
                    </div>
                    {item.badge && (
                      <Badge
                        variant="default"
                        className="h-5 px-2 text-xs font-semibold sidebar-badge"
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                </SidebarMenuButton>
              )}
            </NavLink>
          </SidebarMenuItem>
        )
      )}
    </SidebarMenu>
  );
}
